import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Eye, X, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PreviewKadPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [previewCards, setPreviewCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Get category from URL params
  const categoryFilter = searchParams.get('category') || 'all';
  const activeTab = searchParams.get('tab') || 'cards';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, previewCardsRes, categoriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/templates`),
        axios.get(`${BACKEND_URL}/api/preview-cards`),
        axios.get(`${BACKEND_URL}/api/preview-categories`)
      ]);
      setTemplates(templatesRes.data || []);
      setPreviewCards(previewCardsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplatePreview = (template) => {
    setSelectedTemplate(template);
    setSelectedCard(null);
    setPreviewOpen(true);
  };

  const handleCardPreview = (card) => {
    setSelectedCard(card);
    setSelectedTemplate(null);
    setPreviewOpen(true);
  };

  const setCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat === 'all') {
      params.delete('category');
    } else {
      params.set('category', cat);
    }
    setSearchParams(params);
  };

  const setTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'cards') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  // Filter preview cards by category
  const filteredPreviewCards = categoryFilter === 'all' 
    ? previewCards 
    : previewCards.filter(card => card.preview_category === categoryFilter);

  // Get category name helper
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat?.name || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan design...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </Button>
          <h1 className="text-xl font-bold text-white">Preview Kad</h1>
          <Button
            onClick={() => navigate('/register')}
            className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full px-6 font-semibold"
          >
            Daftar Sekarang
          </Button>
        </div>
      </nav>

      {/* Header Section */}
      <section className="py-12 px-4 bg-gradient-to-b from-[#3D348B] to-[#7678ED] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Koleksi Design Kad Kahwin Digital
          </h2>
          <p className="text-white/80 text-lg">
            Pilih design yang bersesuaian dengan tema majlis anda. Klik untuk preview.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="cards" className="text-sm">
                ✨ Contoh Kad Sebenar
              </TabsTrigger>
              <TabsTrigger value="templates" className="text-sm">
                🎨 Templat
              </TabsTrigger>
            </TabsList>

            {/* Preview Cards Tab */}
            <TabsContent value="cards" className="space-y-6">
              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                  <Button
                    variant={categoryFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCategory('all')}
                    className={categoryFilter === 'all' ? 'bg-[#3D348B]' : ''}
                  >
                    Semua
                  </Button>
                  {categories.map((cat) => (
                    <Button
                      key={cat.category_id}
                      variant={categoryFilter === cat.category_id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategory(cat.category_id)}
                      className={categoryFilter === cat.category_id ? 'bg-[#3D348B]' : ''}
                    >
                      {cat.name}
                    </Button>
                  ))}
                </div>
              )}

              {/* Cards Grid */}
              {filteredPreviewCards.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg mb-4">
                    {categoryFilter !== 'all' ? 'Tiada kad dalam kategori ini.' : 'Tiada contoh kad tersedia.'}
                  </p>
                  {categoryFilter !== 'all' && (
                    <Button onClick={() => setCategory('all')} variant="outline">
                      Lihat Semua Kad
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredPreviewCards.map((card) => (
                    <div
                      key={card.card_id}
                      className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                      onClick={() => handleCardPreview(card)}
                    >
                      <div className="relative aspect-[9/16] bg-gray-100">
                        <img
                          src={card.preview_thumbnail || card.background_thumbnail || card.background_image}
                          alt={`${card.bride_name} & ${card.groom_name}`}
                          className="w-full h-full object-cover"
                        />
                        
                        <div className="absolute inset-0 bg-[#3D348B]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3">
                          <Eye className="w-10 h-10 text-white mb-2" />
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); window.open(`${window.location.origin}/${card.slug}`, '_blank'); }}
                            className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] font-semibold"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" /> Lihat Kad
                          </Button>
                        </div>

                        <div className="absolute top-3 left-3 bg-[#F7B801] text-[#3D348B] text-xs font-bold px-3 py-1 rounded-full">
                          LIVE
                        </div>
                        
                        {card.preview_category && (
                          <div className="absolute bottom-3 left-3 bg-white/90 text-[#3D348B] text-xs px-2 py-1 rounded-full">
                            {getCategoryName(card.preview_category)}
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <h3 className="font-bold text-[#3D348B] truncate">{card.bride_name} & {card.groom_name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{card.wedding_date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates" className="space-y-6">
              {templates.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg mb-4">Tiada templat tersedia buat masa ini.</p>
                  <Button onClick={() => navigate('/register')} className="bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full">
                    Daftar untuk mencipta kad
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                  {templates.map((template) => (
                    <div
                      key={template.template_id}
                      className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                      onClick={() => handleTemplatePreview(template)}
                    >
                      <div className="relative aspect-[9/16] bg-gray-100">
                        {template.preview_image ? (
                          <img
                            src={template.preview_image}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        ) : template.card_defaults?.background_image ? (
                          <img
                            src={template.card_defaults.background_image}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#7678ED]/20 to-[#3D348B]/20">
                            <span className="text-gray-400">Tiada preview</span>
                          </div>
                        )}
                        
                        <div className="absolute inset-0 bg-[#3D348B]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3">
                          <Eye className="w-10 h-10 text-white mb-2" />
                          <Button
                            size="sm"
                            className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] font-semibold"
                          >
                            Lihat Preview
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); navigate('/register'); }}
                            className="border-white text-white hover:bg-white hover:text-[#3D348B]"
                          >
                            Tempah Sekarang
                          </Button>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 className="font-bold text-[#3D348B] truncate">{template.name}</h3>
                        {template.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#F7B801] to-[#F18701]">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-[#3D348B] mb-4">
            Suka dengan design kami?
          </h3>
          <p className="text-[#3D348B]/80 mb-6">
            Daftar sekarang dan mula cipta kad kahwin digital anda sendiri!
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full px-10 py-6 text-lg font-bold shadow-xl"
          >
            Daftar Percuma
          </Button>
        </div>
      </section>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden bg-transparent border-none">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPreviewOpen(false)}
              className="absolute top-2 right-2 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full w-8 h-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>

            <div className="aspect-[9/16] bg-white rounded-2xl overflow-hidden shadow-2xl">
              {selectedCard ? (
                <img
                  src={selectedCard.preview_thumbnail || selectedCard.background_thumbnail || selectedCard.background_image}
                  alt={`${selectedCard.bride_name} & ${selectedCard.groom_name}`}
                  className="w-full h-full object-cover"
                />
              ) : selectedTemplate?.preview_image ? (
                <img
                  src={selectedTemplate.preview_image}
                  alt={selectedTemplate.name}
                  className="w-full h-full object-cover"
                />
              ) : selectedTemplate?.card_defaults?.background_image ? (
                <img
                  src={selectedTemplate.card_defaults.background_image}
                  alt={selectedTemplate.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#7678ED]/20 to-[#3D348B]/20">
                  <span className="text-gray-400">Tiada preview</span>
                </div>
              )}
            </div>

            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                {selectedCard ? `${selectedCard.bride_name} & ${selectedCard.groom_name}` : selectedTemplate?.name}
              </h3>
              <div className="flex gap-2 justify-center flex-wrap">
                {selectedCard && (
                  <Button
                    onClick={() => window.open(`${window.location.origin}/${selectedCard.slug}`, '_blank')}
                    className="bg-white text-[#3D348B] hover:bg-gray-100 rounded-full px-6 font-semibold"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Lihat Kad Live
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full px-8 font-semibold"
                >
                  {selectedCard ? 'Tempah Kad Serupa' : 'Tempah Design Ini'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#3D348B] text-center">
        <p className="text-white/50 text-sm">© 2025 Kawkad. Semua hak terpelihara.</p>
      </footer>
    </div>
  );
}
